import { BearerTokenGetterContext } from '@pelckmans/business-components/context/BearerTokenGetterContext';
import { useContext, useEffect, useRef, useState } from 'react';
import useAnalytics from '../../../../hooks/useAnalytics';
import { reviewExerciseSession } from '../../api/exercises-api';
import { ANALYTICS_EVENT_TYPES, AnalyticsData } from '../../../../hooks/constants/Analytics';
import Dialog from '@pelckmans/elementis/components/dialog';
import { ExerciseIframe } from '../exercise-iframe';
import { ExerciseLoading } from '../exercise-loading';
import { ExerciseTranslationsContext } from '../../contexts/ExerciseTranslationsContext';
import ServiceNotAvailable from '../service-not-available';
import { MedialinkDialogHeaderTags } from '../../../../../../pages/material/components/MedialinkDialogHeaderTags';
import { useExerciseManagerContext } from '../../contexts/ExerciseManagerContext';

interface Props {
  sessionId: string;
  analyticsData?: AnalyticsData;
}

function ExerciseAnswersModal({ sessionId, analyticsData }: Props): JSX.Element | null {
  const [url, setUrl] = useState<string>();
  const [serviceAvailable, setServiceAvailable] = useState<boolean>(true);
  const eventCaptured = useRef<boolean>(false);

  const getBearerToken = useContext(BearerTokenGetterContext);
  const translations = useContext(ExerciseTranslationsContext);
  const { medialink, onClose, module, studioGroup } = useExerciseManagerContext();

  const analytics = useAnalytics();

  useEffect(() => {
    const run = async (): Promise<void> => {
      const token = await getBearerToken();

      try {
        const { reviewUrl } = await reviewExerciseSession(
          token!,
          medialink.moduleId,
          medialink.id,
          sessionId,
          studioGroup,
        );

        setUrl(reviewUrl);
      } catch (err) {
        setServiceAvailable(false);
      }
    };

    run();
  }, [getBearerToken, medialink, sessionId, studioGroup]);

  useEffect(() => {
    const captureEvent = async (): Promise<void> => {
      const token = await getBearerToken();
      if (analyticsData && token && !eventCaptured.current) {
        eventCaptured.current = true;
        analytics.capture(
          {
            eventType: ANALYTICS_EVENT_TYPES.MEDIALINK_ACCESSED,
            objectId: medialink.id,
            analyticsData,
          },
          token,
        );
      }
    };

    captureEvent();
  }, [analytics, analyticsData, getBearerToken, medialink]);

  return (
    <Dialog
      id="exercise-answers-modal"
      modal
      onClose={onClose}
      aria-label={translations.exerciseAnswersModal.a11yLabel}
      size="full"
    >
      <Dialog.Header
        title={medialink.name}
        onClose={onClose}
        aria-label={translations.closeDialog}
        titleTooltip={medialink.name}
        children={
          // should this component also be moved to elementis or should headerProps be used here?
          <MedialinkDialogHeaderTags material={medialink} module={module} />
        }
      />
      <Dialog.Body>
        {!serviceAvailable ? (
          <ServiceNotAvailable />
        ) : (
          <>
            {url ? (
              <ExerciseIframe exerciseUrl={url} medialink={medialink} onExerciseFinished={onClose} />
            ) : (
              <ExerciseLoading />
            )}
          </>
        )}
      </Dialog.Body>
    </Dialog>
  );
}

export default ExerciseAnswersModal;
